import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { ConfigProvider } from 'antd';
import { getEmbedTheme, isTenant188, TENANT_188_THEME } from '../../configs/EmbedConfig';
import { generateThemeCSS, isInIframe, postMessageToParent } from '../../utils/embedUtils';

const EmbedLayout = ({ children, config = {}, isMinimized = false }) => {
  // Use default theme only
  const resolvedTheme = 'default';
  const theme = getEmbedTheme(resolvedTheme);

  // Set data-theme attribute on body for CSS selectors
  useEffect(() => {
    document.body.setAttribute('data-theme', resolvedTheme);
    
    return () => {
      document.body.removeAttribute('data-theme');
    };
  }, [resolvedTheme]);

  useEffect(() => {
    // Apply theme CSS variables
    const themeCSS = generateThemeCSS(theme);
    if (themeCSS) {
      const styleElement = document.createElement('style');
      styleElement.textContent = themeCSS;
      document.head.appendChild(styleElement);

      return () => {
        document.head.removeChild(styleElement);
      };
    }
  }, [theme]);

  useEffect(() => {
    // Apply custom CSS if provided
    if (config.customCSS) {
      const styleElement = document.createElement('style');
      styleElement.textContent = config.customCSS;
      document.head.appendChild(styleElement);

      return () => {
        document.head.removeChild(styleElement);
      };
    }
  }, [config.customCSS]);

  useEffect(() => {
    // Set up iframe-specific styles and behaviors
    if (isInIframe()) {
      // Prevent scrolling of parent page
      document.body.style.overflow = 'hidden';
      
      const isBYDUser = config.extractedUserId === 350;
      const isTenant188Theme = isTenant188(
        config.extractedUserId ?? config.tenantId ?? config.tenant_id
      );
      
      // Handle minimized state styling
      if (isMinimized) {
        document.body.style.backgroundColor = 'transparent';
        document.body.classList.add('embed-minimized');
      } else {
        const shellBg = isBYDUser
          ? '#2a2a2a'
          : isTenant188Theme
            ? TENANT_188_THEME.SHELL_BG
            : 'var(--embed-background-color, #ffffff)';
        document.body.style.backgroundColor = shellBg;
        document.body.classList.remove('embed-minimized');
      }
      
      // Set up resize observer to notify parent of size changes
      const resizeObserver = new ResizeObserver((entries) => {
        for (const entry of entries) {
          const { width, height } = entry.contentRect;
          postMessageToParent('resize', { width, height });
        }
      });

      resizeObserver.observe(document.body);

      // Handle escape key to close widget
      const handleKeyDown = (event) => {
        if (event.key === 'Escape') {
          postMessageToParent('close-request');
        }
      };

      document.addEventListener('keydown', handleKeyDown);

      return () => {
        resizeObserver.disconnect();
        document.removeEventListener('keydown', handleKeyDown);
        document.body.classList.remove('embed-minimized');
      };
    }
  }, [isMinimized, config.extractedUserId, config.tenantId, config.tenant_id]);

  // Inject global styles and CSS-in-JS override only when in iframe
  useEffect(() => {
    if (isInIframe()) {
      // Inject global embed styles
      const globalStyleId = 'embed-global-styles';
      if (!document.getElementById(globalStyleId)) {
        const globalStyleElement = document.createElement('style');
        globalStyleElement.id = globalStyleId;
        globalStyleElement.textContent = embedGlobalStyles;
        // Insert at the beginning to ensure it loads early
        document.head.insertBefore(globalStyleElement, document.head.firstChild);
      }

      // Inject aggressive input field override for iframe isolation
      const inputOverrideId = 'embed-input-override';
      if (!document.getElementById(inputOverrideId)) {
        const inputOverrideElement = document.createElement('style');
        inputOverrideElement.id = inputOverrideId;
        inputOverrideElement.textContent = `
          /* Aggressive input field isolation for iframe - prevents parent CSS interference */
          body input,
          body input[type="text"],
          body input[type="search"],
          body textarea,
          body .ant-input,
          body .ant-input-affix-wrapper .ant-input {
            margin: 0 !important;
            padding: 10px 16px !important;
            padding-top: 10px !important;
            padding-bottom: 10px !important;
            padding-left: 16px !important;
            padding-right: 16px !important;
            height: 40px !important;
            min-height: 40px !important;
            max-height: 40px !important;
            line-height: 20px !important;
            box-sizing: border-box !important;
            vertical-align: middle !important;
            caret-color: #1890ff !important;
            font-size: 14px !important;
            font-family: system-ui, -apple-system, sans-serif !important;
            border: 2px solid #e9ecef !important;
            border-radius: 10px !important;
            background-color: #ffffff !important;
            color: #333 !important;
            width: 100% !important;
            outline: none !important;
            display: block !important;
            position: relative !important;
            transform: none !important;
            top: auto !important;
            left: auto !important;
          }
          
          body .ant-input::placeholder,
          body input::placeholder,
          body textarea::placeholder {
            line-height: 20px !important;
            vertical-align: middle !important;
            color: #999 !important;
            opacity: 1 !important;
          }
          
          body .ant-input:focus,
          body .ant-input-focused,
          body input:focus,
          body input:focus-visible {
            border-color: #1890ff !important;
            box-shadow: 0 0 0 2px rgba(24, 144, 255, 0.2) !important;
            outline: none !important;
          }
          
          body .ant-input-wrapper,
          body .ant-input-affix-wrapper {
            margin: 0 !important;
            padding: 0 !important;
            width: 100% !important;
            box-sizing: border-box !important;
            display: flex !important;
            align-items: center !important;
          }
        `;
        // Insert after global styles but before other styles
        const globalStyle = document.getElementById(globalStyleId);
        if (globalStyle && globalStyle.nextSibling) {
          document.head.insertBefore(inputOverrideElement, globalStyle.nextSibling);
        } else {
          document.head.appendChild(inputOverrideElement);
        }
      }

      // Inject CSS-in-JS override
      const cssInJSStyleId = 'embed-css-in-js-override';
      if (!document.getElementById(cssInJSStyleId)) {
        const cssInJSStyleElement = document.createElement('style');
        cssInJSStyleElement.id = cssInJSStyleId;
        cssInJSStyleElement.textContent = `
          /* Override CSS-in-JS classes that add unwanted spacing in iframe */
          [class*="css-"] {
            margin: 0 !important;
            padding: 0 !important;
          }
          
          /* Specifically target the embed container hierarchy */
          body [class*="css-"],
          #root [class*="css-"],
          .embed-container [class*="css-"],
          div[class*="css-"] {
            margin: 0 !important;
            padding: 0 !important;
          }
          
          /* Target specific known CSS-in-JS class */
          .css-ynzj7f {
            margin: 0 !important;
            padding: 0 !important;
          }
        `;
        // Insert after global styles
        const globalStyle = document.getElementById(globalStyleId);
        if (globalStyle && globalStyle.nextSibling) {
          document.head.insertBefore(cssInJSStyleElement, globalStyle.nextSibling);
        } else {
          document.head.appendChild(cssInJSStyleElement);
        }
      }

      return () => {
        // Cleanup on unmount
        const existingGlobalStyle = document.getElementById(globalStyleId);
        if (existingGlobalStyle) {
          document.head.removeChild(existingGlobalStyle);
        }
        const existingInputOverride = document.getElementById(inputOverrideId);
        if (existingInputOverride) {
          document.head.removeChild(existingInputOverride);
        }
        const existingCssInJSStyle = document.getElementById(cssInJSStyleId);
        if (existingCssInJSStyle) {
          document.head.removeChild(existingCssInJSStyle);
        }
      };
    }
  }, []);

  // Ant Design theme configuration for embed
  const antdTheme = {
    token: {
      colorPrimary: theme.PRIMARY_COLOR,
      colorText: theme.TEXT_COLOR,
      colorBgContainer: theme.BACKGROUND_COLOR,
      borderRadius: parseInt(theme.BORDER_RADIUS),
      fontFamily: theme.FONT_FAMILY,
      fontSize: 14,
      lineHeight: 1.4,
      // Compact sizing for embed
      controlHeight: 32,
      controlHeightSM: 24,
      controlHeightLG: 40,
      // Reduced padding for compact design
      paddingXS: 4,
      paddingSM: 8,
      padding: 12,
      paddingLG: 16,
      paddingXL: 20,
      // Subtle shadows
      boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.03), 0 1px 6px -1px rgba(0, 0, 0, 0.02), 0 2px 4px 0 rgba(0, 0, 0, 0.02)',
      boxShadowSecondary: '0 6px 16px 0 rgba(0, 0, 0, 0.08), 0 3px 6px -4px rgba(0, 0, 0, 0.12), 0 9px 28px 8px rgba(0, 0, 0, 0.05)'
    },
    components: {
      // Input styling
      Input: {
        borderRadius: 20,
        paddingInline: 12,
        paddingBlock: 8
      },
      // Button styling
      Button: {
        borderRadius: 6,
        fontWeight: 500
      },
      // Avatar styling
      Avatar: {
        borderRadius: '50%'
      },
      // Message styling
      Message: {
        borderRadius: 8
      },
      // Spin styling
      Spin: {
        colorPrimary: theme.PRIMARY_COLOR
      }
    }
  };

  return (
    <ConfigProvider theme={antdTheme}>
      <div style={getContainerStyles(config, isMinimized)}>
        <div style={{...getContentStyles(config), ...styles.embedContent}}>
          {children}
        </div>
      </div>
    </ConfigProvider>
  );
};

const getContainerStyles = (config, isMinimized = false) => {
  const isBYDUser = config.extractedUserId === 350;
  const isTenant188Theme = isTenant188(
    config.extractedUserId ?? config.tenantId ?? config.tenant_id
  );
  
  const baseStyles = {
    width: '100%',
    height: '100vh',
    margin: 0,
    padding: 0,
    fontFamily: 'var(--embed-font-family, system-ui, -apple-system, sans-serif)',
    fontSize: '14px',
    lineHeight: '1.4',
    color: 'var(--embed-text-color, #333)',
    backgroundColor: isBYDUser
      ? '#2a2a2a'
      : isTenant188Theme
        ? TENANT_188_THEME.SHELL_BG
        : isMinimized
          ? 'transparent'
          : 'var(--embed-background-color, #ffffff)',
    boxSizing: 'border-box',
    position: 'relative',
    overflow: 'hidden'
  };

  // Apply position-specific styling for iframe context
  if (config.position === 'center') {
    baseStyles.display = 'flex';
    baseStyles.alignItems = 'center';
    baseStyles.justifyContent = 'center';
    
  }

  return baseStyles;
};

const getContentStyles = (config) => {
  const baseStyles = {
    width: '100%',
    height: '100%',
  };

  // For centered position, apply configured dimensions
  if (config.position === 'center') {
    if (config.width) {
      // Handle different width formats (px, %, em, rem, etc.)
      const width = config.width.toString();
      let finalWidth = width;
      if (!width.includes('%') && !width.includes('vw')) {
        finalWidth = width.match(/^\d+$/) ? `${width}px` : width;
      }
      baseStyles.width = finalWidth;
      baseStyles.maxWidth = '100%';
    }
    if (config.height) {
      // Handle different height formats (px, %, vh, em, rem, etc.)
      const height = config.height.toString();
      let finalHeight = height;
      if (!height.includes('%') && !height.includes('vh')) {
        finalHeight = height.match(/^\d+$/) ? `${height}px` : height;
      }
      baseStyles.height = finalHeight;
      baseStyles.maxHeight = '100%';
    }
    baseStyles.boxShadow = '0 4px 24px rgba(0, 0, 0, 0.15)';
    baseStyles.borderRadius = '12px';
    baseStyles.overflow = 'hidden';
  }

  return baseStyles;
};

const styles = {
  embedContainer: {
    // This will be dynamically generated by getContainerStyles
  },
  
  embedContent: {
    // This will be dynamically generated by getContentStyles
    display: 'flex',
    flexDirection: 'column'
  }
};

// Global CSS reset for embed
const embedGlobalStyles = `
  /* Reset styles for embed iframe */
  * {
    box-sizing: border-box;
  }
  
  /* Override CSS-in-JS classes in iframe context */
  [class*="css-"] {
    margin: 0 !important;
    padding: 0 !important;
  }
  
  body [class*="css-"],
  #root [class*="css-"],
  div[class*="css-"] {
    margin: 0 !important;
    padding: 0 !important;
  }
  
  body {
    margin: 0;
    padding: 0;
    font-family: var(--embed-font-family, system-ui, -apple-system, sans-serif);
    font-size: 14px;
    line-height: 1.4;
    color: var(--embed-text-color, #333);
    background-color: var(--embed-background-color, #ffffff);
    overflow: hidden;
  }
  
  /* BYD user dark theme - ensure no white backgrounds */
  body[data-byd-theme="true"],
  body[data-byd-theme="true"] #root,
  html[data-byd-theme="true"] {
    background-color: #2a2a2a !important;
  }

  /* Tenant 188 — match dark marketing site iframe chrome */
  body[data-tenant188-theme="true"],
  body[data-tenant188-theme="true"] #root,
  html[data-tenant188-theme="true"] {
    background-color: #0f172a !important;
  }
  
  /* Minimized state styles */
  body.embed-minimized {
    background-color: transparent !important;
  }
  
  body.embed-minimized #root {
    background-color: transparent !important;
  }

  /* Mobile optimizations */
  @media (max-width: 480px) {
    body {
      font-size: 13px;
    }

    .message-content {
      padding: 10px 14px !important;
      max-width: 100% !important;
    }

    .ant-input {
      font-size: 16px !important; /* Prevents zoom on iOS */
    }

    .ant-btn {
      height: 36px !important;
      padding: 4px 12px !important;
    }
  }
  
  /* Scrollbar styling */
  ::-webkit-scrollbar {
    width: 6px;
  }
  
  ::-webkit-scrollbar-track {
    background: transparent;
  }
  
  ::-webkit-scrollbar-thumb {
    background: var(--embed-border-color, #e0e0e0);
    border-radius: 3px;
  }
  
  ::-webkit-scrollbar-thumb:hover {
    background: var(--embed-secondary-color, #999);
  }
  
  /* Typing indicator animation */
  @keyframes typing-dots {
    0%, 60%, 100% {
      transform: translateY(0);
      opacity: 0.4;
    }
    30% {
      transform: translateY(-10px);
      opacity: 1;
    }
  }
  
  .typing-dots span {
    display: inline-block;
    width: 4px;
    height: 4px;
    border-radius: 50%;
    background-color: var(--embed-secondary-color, #999);
    animation: typing-dots 1.4s infinite ease-in-out;
  }
  
  .typing-dots span:nth-child(1) {
    animation-delay: -0.32s;
  }
  
  .typing-dots span:nth-child(2) {
    animation-delay: -0.16s;
  }
  
  .typing-dots span:nth-child(3) {
    animation-delay: 0;
  }
  
  /* Message content styling */
  .message-content {
    word-wrap: break-word;
    word-break: break-word;
  }
  
  .message-content p {
    margin: 0 0 8px 0;
  }
  
  .message-content p:last-child {
    margin-bottom: 8px;
  }
  
  .message-content ul,
  .message-content ol {
    margin: 8px 0;
    padding-left: 20px;
  }
  
  .message-content li {
    margin: 4px 0;
  }
  
  .message-content code {
    background-color: var(--embed-border-color, #f0f0f0);
    padding: 2px 4px;
    border-radius: 3px;
    font-family: 'Monaco', 'Consolas', monospace;
    font-size: 12px;
  }
  
  .message-content pre {
    background-color: var(--embed-border-color, #f0f0f0);
    padding: 12px;
    border-radius: 6px;
    overflow-x: auto;
    margin: 8px 0;
  }
  
  .message-content pre code {
    background: none;
    padding: 0;
  }
  
  .message-content blockquote {
    border-left: 3px solid var(--embed-primary-color, #1890ff);
    margin: 8px 0;
    padding: 8px 12px;
    background-color: var(--embed-border-color, #f0f0f0);
    border-radius: 0 6px 6px 0;
  }
  
  .message-content a {
    color: var(--embed-primary-color, #1890ff);
    text-decoration: none;
  }
  
  .message-content a:hover {
    text-decoration: underline;
  }
  
  /* Input field styles - Aggressive overrides for iframe isolation */
  input,
  input[type="text"],
  input[type="search"],
  textarea,
  .ant-input,
  .ant-input-affix-wrapper .ant-input {
    margin: 0 !important;
    padding: 10px 16px !important;
    padding-top: 10px !important;
    padding-bottom: 10px !important;
    padding-left: 16px !important;
    padding-right: 16px !important;
    height: 40px !important;
    min-height: 40px !important;
    max-height: 40px !important;
    line-height: 20px !important;
    box-sizing: border-box !important;
    vertical-align: middle !important;
    caret-color: var(--embed-primary-color, #1890ff) !important;
    font-size: 14px !important;
    font-family: var(--embed-font-family, system-ui, -apple-system, sans-serif) !important;
    border: 2px solid #e9ecef !important;
    border-radius: 10px !important;
    background-color: #ffffff !important;
    color: #333 !important;
    width: 100% !important;
    min-width: 0 !important;
    max-width: 100% !important;
    outline: none !important;
    display: block !important;
    position: relative !important;
    transform: none !important;
    top: auto !important;
    left: auto !important;
  }
  
  /* Ensure input text is properly positioned */
  .ant-input::placeholder,
  input::placeholder,
  textarea::placeholder {
    line-height: 20px !important;
    vertical-align: middle !important;
    color: #999 !important;
    opacity: 1 !important;
  }
  
  /* Input wrapper container - prevent parent CSS interference */
  .ant-input-wrapper,
  .ant-input-affix-wrapper {
    margin: 0 !important;
    padding: 0 !important;
    width: 100% !important;
    box-sizing: border-box !important;
    display: flex !important;
    align-items: center !important;
  }
  
  /* Focus styles with !important */
  .ant-input:focus,
  .ant-input-focused,
  input:focus,
  input:focus-visible {
    border-color: var(--embed-primary-color, #1890ff) !important;
    box-shadow: 0 0 0 2px rgba(24, 144, 255, 0.2) !important;
    outline: none !important;
  }
  
  /* Prevent any transform or positioning that might hide cursor */
  .ant-input,
  input[type="text"] {
    transform: none !important;
    position: relative !important;
    top: auto !important;
    left: auto !important;
  }
  
  /* Button styles */
  .ant-btn-primary {
    background-color: var(--embed-primary-color, #1890ff);
    border-color: var(--embed-primary-color, #1890ff);
  }
  
  .ant-btn-primary:hover {
    background-color: var(--embed-primary-color, #1890ff);
    border-color: var(--embed-primary-color, #1890ff);
    opacity: 0.8;
  }
  
  /* Responsive design */
  @media (max-width: 480px) {
    .embed-container {
      font-size: 13px;
    }
    
    .message-content {
      font-size: 13px;
    }
  }
`;

// Global styles are now injected via useEffect to ensure proper timing

EmbedLayout.propTypes = {
  config: PropTypes.object,
  children: PropTypes.node.isRequired,
  isMinimized: PropTypes.bool,
};

export default EmbedLayout;
